import type { ExtensionUIContext, HostInputFormRequest } from "../../core/extensions/index.js";
import { type HostInputFormMount, mountHostInputForm } from "../interactive/components/host-input-form-mount.ts";
import type { IsolatedInteractiveRuntime } from "./isolated-runtime.js";
import type { InteractiveEngineMessage } from "./protocol.ts";

/** Host-side controller that keeps form input/state entirely in the terminal process. */
export class InputFormHostController {
	private readonly mounted = new Map<string, HostInputFormMount>();
	private readonly runtime: IsolatedInteractiveRuntime;
	private readonly ui: ExtensionUIContext;
	private readonly unsubscribe: () => void;
	private readonly unsubscribeGenerationEnded: () => void;
	constructor(runtime: IsolatedInteractiveRuntime, ui: ExtensionUIContext) {
		this.runtime = runtime;
		this.ui = ui;
		this.unsubscribe = runtime.onEngineMessage((message) => this.handleMessage(message));
		// A dead generation cannot answer a form, and its componentIds mean nothing
		// to a replacement child, so unmount on death rather than waiting for the
		// next `engine_ready` (which never arrives if the restart hangs or fails).
		this.unsubscribeGenerationEnded = runtime.onGenerationEnded(() => this.disposeAll());
	}

	dispose(): void {
		this.unsubscribe();
		this.unsubscribeGenerationEnded();
		this.disposeAll();
	}

	private handleMessage(message: InteractiveEngineMessage): void {
		if (message.type === "engine_ready") {
			this.disposeAll();
			return;
		}
		if (message.type === "engine_input_form_close") {
			this.mounted.get(message.componentId)?.close();
			this.mounted.delete(message.componentId);
			return;
		}
		if (message.type === "engine_input_form_open") {
			this.open(message.componentId, {
				title: message.title,
				fields: message.fields,
				...(message.heading !== undefined ? { heading: message.heading } : {}),
				...(message.submitLabel !== undefined ? { submitLabel: message.submitLabel } : {}),
			});
		}
	}

	private open(componentId: string, request: HostInputFormRequest): void {
		if (this.mounted.has(componentId)) return;
		const mount = mountHostInputForm(this.ui, request, {
			onSubmit: (values) => {
				this.mounted.delete(componentId);
				this.runtime.sendEngineCommand({ type: "engine_input_form_submit", componentId, values });
			},
			onCancel: () => {
				this.mounted.delete(componentId);
				this.runtime.sendEngineCommand({ type: "engine_input_form_cancel", componentId });
			},
		});
		this.mounted.set(componentId, mount);
	}

	private disposeAll(): void {
		for (const mount of this.mounted.values()) mount.close();
		this.mounted.clear();
	}
}
